import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import { useMemo, useState } from "react";
import moment from "moment";

import { analyzeSetCharacteristics } from "../game";
import { STATS_DEFAULTS } from "../hooks/useAllTimeStats";
import SetCard from "./SetCard";
import Subheading from "./Subheading";

function formatTime(t, hideSubsecond) {
  t = Math.max(t, 0);
  const hours = Math.floor(t / (3600 * 1000));
  const rest = t % (3600 * 1000);
  const format = hideSubsecond ? "mm:ss" : "mm:ss.SS";
  return (hours ? `${hours}:` : "") + moment.utc(rest).format(format);
}

const useStyles = makeStyles((theme) => ({
  recordPanel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    minWidth: 0,
  },
  record: {
    overflowY: "auto",
    flexGrow: 1,
    overflowWrap: "anywhere",
    padding: "0 4px",
    minWidth: 0,
  },
  logEntry: {
    marginBottom: "0.35em",
    padding: "0 12px",
    textAlign: "center",
    background: theme.setFoundEntry || "rgba(130, 170, 100, 0.15)",
  },
  logEntryText: {
    display: "flex",
    justifyContent: "center",
    whiteSpace: "nowrap",
  },
  analysis: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },
  analysisItem: {
    marginBottom: theme.spacing(0.5),
  },
  statLink: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

function GameRecord({
  history,
  gameMode,
  startedAt,
  allTimeStats,
  user,
  authEnabled,
  onSignIn,
  onSignOut,
}) {
  const classes = useStyles();
  const [showAllTime, setShowAllTime] = useState(false);

  const analysis = useMemo(() => {
    if (!history || history.length === 0) {
      return {
        noneShared: 0,
        color: 0,
        shape: 0,
        shading: 0,
        number: 0,
      };
    }
    
    const stats = {
      noneShared: 0,
      color: 0,
      shape: 0,
      shading: 0,
      number: 0,
    };
    
    history.forEach((event) => {
      if (event.c1 && event.c2 && event.c3) {
        const analysis = analyzeSetCharacteristics([event.c1, event.c2, event.c3]);

        // console.log('Analyzing set:', [event.c1, event.c2, event.c3], 'Analysis:', analysis);

        if (analysis.sharedCount === 0) {
          stats.noneShared++;
        } else {
          // Count each shared characteristic separately (can have multiple)
          if (analysis.colorShared) stats.color++;
          if (analysis.numberShared) stats.number++;
          if (analysis.shapeShared) stats.shape++;
          if (analysis.shadingShared) stats.shading++;
        }
      }
    });
    
    return stats;
  }, [history]);
  
  const sortedItems = useMemo(() => {
    if (!history || !Array.isArray(history)) return [];
    return [...history].sort((a, b) => a.time - b.time);
  }, [history]);

  const statsEnabled = authEnabled && gameMode === "normal";
  const allTimeView = statsEnabled && showAllTime && !!user;
  const allTime = { ...STATS_DEFAULTS, ...allTimeStats };

  const displayed = allTimeView ? allTime : analysis;
  const rows = [
    ["None shared", displayed.noneShared],
    ["Color", displayed.color],
    ["Shape", displayed.shape],
    ["Shading", displayed.shading],
    ["Number", displayed.number],
  ];
  if (allTimeView) {
    rows.push(
      ["Total sets", allTime.totalSets],
      ["Games finished", allTime.gamesFinished],
      [
        "Avg sets at quit",
        allTime.gamesQuit > 0
          ? (allTime.setsAtQuit / allTime.gamesQuit).toFixed(1)
          : "—",
      ]
    );
  }

  return (
    <section className={classes.recordPanel}>
      <Subheading>
        Game Record
        {statsEnabled && (
          <>
            {" "}
            {!user ? (
              <span className={classes.statLink} onClick={onSignIn}>
                (track all-time?)
              </span>
            ) : (
              <span
                className={classes.statLink}
                onClick={() => setShowAllTime(!showAllTime)}
              >
                {showAllTime ? "(hide all-time)" : "(show all-time)"}
              </span>
            )}
          </>
        )}
      </Subheading>
      <Divider style={{ marginBottom: 8 }} />
      <div className={classes.analysis}>
        <div>
          {rows.map(([label, value]) => (
            <div
              key={label}
              style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}
            >
              <Typography variant="body2">{label}</Typography>
              <Typography variant="body2"><strong>{value}</strong></Typography>
            </div>
          ))}
          {allTimeView && (
            <div style={{ textAlign: "right" }}>
              <span className={classes.statLink} onClick={onSignOut}>
                (sign out)
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={classes.record} style={{ overflowY: "auto" }}>
        {sortedItems.length === 0 ? (
          <div className={classes.logEntry}>
            <div className={classes.logEntryText}>
              <Typography variant="subtitle2">
                Find a set, you doofus!
              </Typography>
            </div>
          </div>
        ) : (
          sortedItems.map((item, index) => {
            const sharedCharacteristics = item.c1 && item.c2 && item.c3 ?
              (() => {
                const analysis = analyzeSetCharacteristics([item.c1, item.c2, item.c3]);
                const shared = [];
                if (analysis.colorShared) shared.push('color');
                if (analysis.numberShared) shared.push('number');
                if (analysis.shapeShared) shared.push('shape');
                if (analysis.shadingShared) shared.push('shading');
                return shared.length > 0 ? `(${shared.join(', ')})` : '';
              })() : '';

            return (
              <div key={index} className={classes.logEntry}>
                <div className={classes.logEntryText} style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2">
                    {formatTime(item.time - startedAt, true)} - Set found
                  </Typography>
                  {sharedCharacteristics && (
                    <Typography variant="caption" style={{ color: '#888', marginTop: -1}}>
                      {sharedCharacteristics}
                    </Typography>
                  )}
                </div>
                {item.c1 && item.c2 && item.c3 && (
                  <div>
                    <SetCard size="sm" value={item.c1} />
                    <SetCard size="sm" value={item.c2} />
                    <SetCard size="sm" value={item.c3} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

export default GameRecord;

